import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import React, { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import './form_upload.scss';
import FormHelperText from '@mui/material/FormHelperText';

import { UploadResponse } from '@business/entities/common';
import { Button } from '@core/components/Button';
import { tapError } from '@core/utils/rxjs/operators';

export interface FormUploadBaseProps<T extends File | any | File[] | any[]> {
  onChange?(value: T | null): void;
  style?: CSSProperties;
  accept?: string;
  image?: boolean;
  autoUpload?: boolean;
  autoUploadKey?: string;
  onRemove?(): void;
  viewType?: 'drop-zone' | 'button';
  onUploadStateChange?(isUploading: boolean): void;
}

export interface FormUploadSingleProps<T extends File | any> extends FormUploadBaseProps<T>, FieldProps<FormControl<T>> {
  type?: 'single';
  btnLabel?: string;
}
export interface FormUploadMultipleProps<T extends File[] | any[]> extends FormUploadBaseProps<T>, FieldProps<FormControl<T>> {
  type?: 'multiple';
  btnLabel?: string;
}

export type FormUploadProps<T extends any> = FormUploadSingleProps<T> | FormUploadMultipleProps<any>;

// export type FormUploadProps = FieldProps<FormControl<string>>;

export function FormUploadMultiple<T extends any>(props: FormUploadProps<T>) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldValue = props.type === 'multiple' ? fieldState.value || [] : fieldState.value || null;
  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;
  const isUploadResponse = useMemo(() => {
    return R.get(fieldValue, 'fileUrl') && R.get(fieldValue, 'fileName');
  }, [fieldValue]);

  const inputFileRef = useRef<HTMLInputElement>(null);

  const fieldReadonly = fieldConfig.readonly || null;
  const fieldDisabled = fieldConfig.disabled || null;
  const [loader, setLoader] = useState(false);

  const onUpload = useCallback(
    (file: File) => {
      if (props.autoUpload && props.autoUploadKey) {
        setLoader(true);
        props.onUploadStateChange?.(true);
        UploadAPI.upload(props.autoUploadKey, file)
          .pipe(
            tap(res => {
              if (props.type === 'multiple') {
                const files = fieldValue ?? [];
                files.push(res as UploadResponse);
                formControl.setValue(files);
              } else {
                formControl.setValue(res as any);
              }
            }),
            tap(() => {
              setLoader(false);
              props.onUploadStateChange?.(false);
            }),
            tapError(() => {
              setLoader(false);
              props.onUploadStateChange?.(false);
            }),
          )
          .subscribe();
      } else {
        formControl.setValue(file as any);
      }
    },
    [props, fieldValue, formControl],
  );

  const onChange = useCallback(
    (files: FileList | null) => {
      if (!files) return null;
      if (props.type === 'multiple') {
        const data: File[] = fieldValue;
        for (let i = 0; i < files.length; i++) {
          onUpload(files[i]);
        }

        formControl.setValue(data as any);
      } else {
        onUpload(files[0]);
        // formControl.setValue(files[0] as any);
      }
    },
    [fieldValue, formControl, onUpload, props.type],
  );

  const label = useMemo(() => {
    let fieldLabel = fieldConfig.label;
    if (fieldValue) {
      if (props.type === 'multiple') {
        fieldLabel = (fieldValue as File[]).map(f => f.name).join(', ');
      } else {
        fieldLabel = (fieldValue as File).name;
      }
    }

    if (!R.isEmpty(fieldLabel)) {
      return fieldLabel;
    }
    return fieldConfig.label;
  }, [fieldConfig.label, fieldValue, props.type]);

  return (
    <React.Fragment>
      <div>
        <div className="custom-file mb-3" style={{ display: 'none' }}>
          <input
            accept={props.accept || (props.image ? 'image/*' : undefined)}
            type="file"
            multiple={props.type === 'multiple'}
            className="custom-file-input"
            onChange={e => onChange(e.target.files)}
            id="customFile"
            ref={inputFileRef}
          />
          <label className="custom-file-label" htmlFor="customFile">
            {label}
          </label>
        </div>

        {props.viewType == 'drop-zone' ? (
          <div className="upload-screen">
            <div className="upload-video-container" onClick={props.disabled ? undefined : () => inputFileRef.current?.click()}>
              {loader ? <i className="upload-icon fas fa-spinner fa-spin"></i> : <i className="upload-icon fas fa-upload"></i>}
            </div>
          </div>
        ) : (
          <label htmlFor="customFile">
            <Button color="primary" outline onClick={() => inputFileRef.current?.click()} loading={loader}>
              {props?.btnLabel ?? 'Choose File'}
            </Button>
          </label>
        )}
        {isUploadResponse && (
          <a href={fieldValue.fileUrl} target="_blank" rel="noopener noreferrer">
            {fieldValue.fileName}
          </a>
        )}
        {R.isString(fieldValue) && props.image && !props.onRemove && <img src={fieldValue} style={{ width: 100, marginBottom: 20 }} />}
        {R.isString(fieldValue) && props.image && props.onRemove && (
          <div className="media-file" style={{ position: 'relative', width: '130px' }}>
            <i className="fa fa-trash-alt float-right upload-media-remove-icon" onClick={props.onRemove}></i>
            <img src={fieldValue} style={{ width: 100, marginBottom: 20 }} />
          </div>
        )}
        {!!fieldError && (
          <FormHelperText className="mui-error" style={{ color: '#f44336' }}>
            {fieldError}
          </FormHelperText>
        )}
      </div>
    </React.Fragment>
  );
}
